import React from "react";
import { Box, Button, Popover, useMediaQuery, useTheme } from "@mui/material";
import { Calendar, LongRight } from "@tiny/icons";
import { formatDate } from "@tiny/utils/dateUtils";
import { DateRangePicker, Range, DefinedRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { addDays, startOfDay } from "date-fns";
import { defaultStaticRanges } from "./defaultStaticRange";
import { TinyDateRangePickerProps } from "./types";
import {
  saveDateRangeToStorage,
  getInitialDateRange,
} from "../../utils/dateRangeStorage";

function TinyDateRangePicker({
  onChange,
  selectedRange,
  color,
  direction,
  minDate,
  buttonLabel,
  maxDate,
  staticRanges,
}: TinyDateRangePickerProps) {
  const theme = useTheme();
  const sm = useMediaQuery("(max-width:800px)");
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const open = Boolean(anchorEl);
  const id = open ? "tiny-date-picker-popover" : undefined;

  const defaultProps: {
    startDate: Date;
    endDate: Date;
    color: string | undefined;
    direction: "horizontal" | "vertical" | undefined;
    minDate: Date;
    maxDate: Date;
    buttonLabel: string | undefined;
  } = {
    startDate: startOfDay(new Date()),
    endDate: startOfDay(addDays(new Date(), 7)),
    color: theme.palette.primary[100],
    direction: sm ? "vertical" : "horizontal",
    minDate: new Date(),
    buttonLabel: buttonLabel || "Select Date",
    maxDate: new Date(),
  };

  const handlePopoverClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = (resetValue: {
    startDate: Date;
    endDate: Date;
  }) => {
    setRangeSelected([
      {
        ...defaultRange,
        ...resetValue,
      },
    ]);
    setAnchorEl(null);
  };

  const handleSubmit = () => {
    if (
      rangeSelected &&
      rangeSelected[0]?.startDate &&
      rangeSelected[0]?.endDate
    ) {
      const val = {
        startDate: rangeSelected[0]?.startDate,
        endDate: rangeSelected[0]?.endDate,
      };
      saveDateRangeToStorage(val);
      setResetValue(val);
      onChange(val);
      handlePopoverClose(val);
    }
  };

  const initialRange = getInitialDateRange(selectedRange);

  const defaultRange = {
    startDate: initialRange.startDate,
    endDate: initialRange.endDate,
    key: "selection",
    color: color || defaultProps.color,
  };

  const [rangeSelected, setRangeSelected] = React.useState<Range[]>([
    defaultRange,
  ]);

  const [resetValue, setResetValue] = React.useState({
    startDate: initialRange.startDate,
    endDate: initialRange.endDate,
  });

  return (
    <Box>
      <Button
        onClick={handlePopoverClick}
        variant="outlined"
        size="small"
        sx={{
          px: 5,
          py: 2,
          fontWeight: "normal",
          color: "grey.90",
          backgroundColor: "common.white",
          borderColor: "grey.50",
          "&:hover": {
            borderColor: "grey.70",
            boxShadow: "none",
            backgroundColor: "common.white",
          },
          "&:active": {
            boxShadow: "none",
            borderColor: "grey.50",
          },
          display: { xs: "flex", md: "auto" },
          width: { xs: "100%", sm: "auto" },
        }}
        endIcon={<Calendar sx={{ color: "grey.70" }} />}
        id="campaign-calendar-datepicker"
      >
        <Box>{formatDate(resetValue.startDate, "yyyy-MM-dd")}</Box>
        <LongRight
          style={{
            fontSize: 16,
            marginLeft: 16,
            marginRight: 16,
            color: "grey.60",
          }}
        />
        <Box>{formatDate(resetValue.endDate, "yyyy-MM-dd")}</Box>
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => handlePopoverClose(resetValue)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box>
          {sm ? (
            <Box>
              <DefinedRange
                onChange={(item) => {
                  setRangeSelected([item?.selection]);
                }}
                ranges={rangeSelected}
                staticRanges={staticRanges || defaultStaticRanges}
                inputRanges={[]}
              />
              <Box
                display="flex"
                gap={2}
                justifyContent="flex-end"
                sx={{ p: 2, borderTop: "1px solid", borderColor: "grey.20" }}
              >
                <Button
                  variant="text"
                  onClick={() => handlePopoverClose(resetValue)}
                >
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSubmit}>
                  Submit
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <DateRangePicker
                months={2}
                direction={direction || defaultProps.direction}
                minDate={minDate}
                maxDate={maxDate}
                showDateDisplay={false}
                ranges={rangeSelected}
                onChange={(item) => {
                  setRangeSelected([item?.selection]);
                }}
                fixedHeight
                inputRanges={[]}
                staticRanges={staticRanges || defaultStaticRanges}
              />
              <Box
                display="flex"
                gap={4}
                justifyContent="flex-end"
                sx={{
                  p: 5,
                  pt: 0,
                }}
              >
                <Button
                  variant="text"
                  onClick={() => handlePopoverClose(resetValue)}
                >
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSubmit}>
                  Submit
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </Box>
  );
}

export default TinyDateRangePicker;
